import { MancalaGame } from "mancala.js";
import { FinishedGameResponse, FinishedGameSummary, Game, GameRecord, LiveGameSummary } from "@mancala/core";
import { HttpServiceImpl } from "../service/HttpService";

export type LoadedGame = { kind: "live", game: Game } | { kind: "finished", record: GameRecord };

export interface GameStore {
    // A live game, or the saved record of a finished one; undefined if there is no such game.
    get(id: string): Promise<LoadedGame | undefined>;
    getActiveGame(token: string): Promise<LiveGameSummary | null>;
    getMyRecentGames(token: string, limit: number): Promise<FinishedGameSummary[]>;
    // The most watched finished games, also while games are live.
    getPopularFinishedGames(limit: number): Promise<FinishedGameSummary[]>;
}

export class GameStoreImpl implements GameStore {
    httpService: HttpServiceImpl;
    constructor(props: { httpService: HttpServiceImpl }) {
        this.httpService = props.httpService;
    }

    async get(id: string): Promise<LoadedGame | undefined> {
        const response = await this.httpService.get(`/game/${id}`);
        if (response.status === 404) return undefined;
        if (!response.ok) throw new Error(`Loading game ${id} failed: ${response.status}`);
        const json = await response.json();
        if ((json as FinishedGameResponse).status === "finished") {
            return { kind: "finished", record: (json as FinishedGameResponse).record };
        }
        const game = json as Game;
        game.mancalaGame = MancalaGame.createFromMancalaGame(game.mancalaGame);
        return { kind: "live", game };
    }

    async getActiveGame(token: string): Promise<LiveGameSummary | null> {
        const json = await this.getJson("/games/me/active", token);
        return json.game;
    }

    async getMyRecentGames(token: string, limit: number): Promise<FinishedGameSummary[]> {
        const json = await this.getJson(`/games/me?limit=${limit}`, token);
        return json.games;
    }

    async getPopularFinishedGames(limit: number): Promise<FinishedGameSummary[]> {
        const json = await this.getJson(`/games/popular?kind=finished&limit=${limit}`);
        return json.games;
    }

    private async getJson(route: string, token?: string): Promise<any> {
        const response = await this.httpService.get(route, token);
        if (!response.ok) throw new Error(`GET ${route} failed: ${response.status}`);
        return response.json();
    }
}
